import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, LogIn } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import PiLoginButton from '../atoms/PiLoginButton';
import GoogleLoginButton from '../atoms/GoogleLoginButton';
import { Button } from '../ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import Logo from '../ui/Logo';

const SignInModal = ({ isOpen, onClose, from }) => {
    const navigate = useNavigate();
    const { isPiBrowser, isPiBrowserDetecting, refreshUserData } = useAuth();

    useEffect(() => {
        // If modal is open and we detect user is authenticated, close it
        // This handles the case where login completes successfully
        // We rely on the onSuccess callbacks mostly, but this is a safety check
    }, [isOpen]);

    const handleLoginSuccess = async (result) => {
        try {
            await refreshUserData();

            // Close modal
            onClose();

            // If there's a specific post-login action, it might be handled by the parent
            // But typically we simply refresh auth state and the parent updates UI
        } catch (error) {
            console.error('❌ Failed to refresh auth state:', error);
            onClose();
        }
    };

    const handleLoginError = (error) => {
        console.error('❌ Login error:', error);
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
                onClick={onClose}
            >
                <motion.div
                    initial={{ scale: 0.9, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.9, opacity: 0, y: 20 }}
                    transition={{ type: "spring", duration: 0.5 }}
                    className="w-full max-w-md mx-auto relative"
                    onClick={(e) => e.stopPropagation()}
                >
                    <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur-xl overflow-hidden">
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 p-2 rounded-full hover:bg-neutral-100 transition-colors z-10"
                        >
                            <X className="w-5 h-5 text-neutral-500" />
                        </button>

                        <div className="p-8 text-center space-y-6">
                            {/* Logo & Header */}
                            <div className="flex flex-col items-center gap-4">
                                <div className="h-16 w-16 bg-gradient-to-br from-primary-teal/10 to-primary-teal/5 rounded-2xl flex items-center justify-center shadow-inner">
                                    <Logo variant="mark" size="small" />
                                </div>
                                <div className="space-y-2">
                                    <h2 className="text-2xl font-heading font-bold text-neutral-900">
                                        Welcome to Crowblock
                                    </h2>
                                    <p className="text-neutral-600">
                                        Sign in to continue supporting projects
                                    </p>
                                </div>
                            </div>

                            {/* Login Buttons */}
                            <div className="space-y-4 pt-2">
                                {/* Pi Login - Show if Pi Browser or Detecting */}
                                {(isPiBrowser === true || isPiBrowserDetecting) && (
                                    <motion.div
                                        whileHover={{ scale: 1.01 }}
                                        whileTap={{ scale: 0.99 }}
                                    >
                                        <PiLoginButton
                                            size="lg"
                                            className="w-full shadow-lg hover:shadow-xl transition-all duration-300 font-medium"
                                            onSuccess={handleLoginSuccess}
                                            onError={handleLoginError}
                                        />
                                    </motion.div>
                                )}

                                {/* Google Login - Show if NOT Pi Browser */}
                                {isPiBrowser === false && (
                                    <>
                                        <motion.div
                                            whileHover={{ scale: 1.01 }}
                                            whileTap={{ scale: 0.99 }}
                                        >
                                            <GoogleLoginButton
                                                size="lg"
                                                className="w-full shadow-md"
                                                onSuccess={handleLoginSuccess}
                                                onError={handleLoginError}
                                            />
                                        </motion.div>

                                        <div className="relative py-2">
                                            <div className="absolute inset-0 flex items-center">
                                                <div className="w-full border-t border-neutral-200"></div>
                                            </div>
                                            <div className="relative flex justify-center text-xs uppercase">
                                                <span className="bg-white/95 px-2 text-neutral-500">
                                                    Or
                                                </span>
                                            </div>
                                        </div>

                                        <div className="text-center">
                                            <p className="text-xs text-neutral-500 mb-3">
                                                Running in Pi Browser?
                                            </p>
                                            <p className="text-xs text-neutral-400">
                                                Open this page in the Pi Browser app to sign in with Pi Network.
                                            </p>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Terms */}
                            <div className="text-center text-xs text-neutral-400">
                                <p>
                                    By signing in, you agree to our{' '}
                                    <a href="/terms" className="text-primary-teal hover:underline" onClick={onClose}>
                                        Terms
                                    </a>{' '}
                                    and{' '}
                                    <a href="/privacy" className="text-primary-teal hover:underline" onClick={onClose}>
                                        Privacy Policy
                                    </a>
                                </p>
                            </div>
                        </div>
                    </Card>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default SignInModal;
